import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type {
  EventCard as EventCardDto,
  EventCommunityGoalDto,
  EventDetail,
  EventObjectiveDto,
  EventRewardDefDto,
  EventsResponse,
  EventTierDto,
} from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, Panel } from '../components/ui';
import { EggArt } from '../components/EggArt';
import { formatDate, formatNumber } from '../lib/format';

/* =========================================================================
   Events — the player's side.
   =========================================================================

   Two pages: the index of what is on, and one event in full. Both are themed
   from the event's own `theme` key, which is a class on a wrapper rather than
   inline styles — the same rule the cosmetics follow, so a theme can never
   change layout and can never cost legibility.

   Nothing here computes anything. Progress, eligibility, whether a reward is
   claimable and what a bonus is worth all arrive decided; this renders them and
   posts intent. That is what makes a stale tab harmless.
   ========================================================================= */

function countdown(seconds: number): string {
  if (seconds <= 0) return 'Ending now';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}

function untilStart(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  if (days > 0) return `Starts in ${days}d ${hours}h`;
  if (hours > 0) return `Starts in ${hours}h`;
  return 'Starting soon';
}

/** Human labels for the modifier keys, so a bonus reads as a sentence. */
const BONUS_LABEL: Record<string, string> = {
  'shiny.odds': 'Shiny odds',
  'shiny.species': 'Shiny odds (featured species)',
  'breeding.speed': 'Breeding speed',
  'breeding.production': 'Egg production',
  'hatch.progress': 'Hatch progress',
  'hatch.stepSpeed': 'Incubation speed',
  'feed.exp': 'Feeding EXP',
  'feed.reciprocal': 'Team EXP from helping',
  'warm.progress': 'Egg warming',
  'coins.rewards': 'Coin rewards',
  'bazaar.fee': 'Bazaar fee',
  'eggmarket.rate': 'Egg Market refresh',
  'npc.frequency': 'Living Trainer visits',
  'exp.all': 'All EXP',
};

/* -------------------------------------------------------------------------
   The index
   ------------------------------------------------------------------------- */

export function EventsPage() {
  const state = useAsync<EventsResponse>(() => api.get('/events'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const active = state.data?.active ?? [];
  const upcoming = state.data?.upcoming ?? [];
  const recent = state.data?.recent ?? [];

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Events</h1>
          <div className="page-head__sub">
            Limited-time festivals that change the game you already play.
          </div>
        </div>
      </div>

      {state.loading ? (
        <div className="pxwell skeleton" style={{ height: 260 }} />
      ) : (
        <>
          {active.length > 0 ? (
            <div className="event-grid">
              {active.map((event) => (
                <EventCard key={event.key} event={event} featured />
              ))}
            </div>
          ) : (
            <Panel>
              <EmptyState art="✦" title="Nothing on right now">
                No festival is running. When one starts it will appear here, and on your Home page.
              </EmptyState>
            </Panel>
          )}

          {upcoming.length > 0 && (
            <Panel title="Coming up">
              <div className="event-grid">
                {upcoming.map((event) => (
                  <EventCard key={event.key} event={event} />
                ))}
              </div>
            </Panel>
          )}

          {recent.length > 0 && (
            <Panel title="Recently finished" collapsibleKey="events-recent">
              <div className="event-grid">
                {recent.map((event) => (
                  <EventCard key={event.key} event={event} />
                ))}
              </div>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

function EventCard({ event, featured = false }: { event: EventCardDto; featured?: boolean }) {
  const live = event.status === 'ACTIVE' || event.status === 'PAUSED';
  const boss = event.worldBoss ?? null;

  return (
    <Link
      // A World Boss card goes to the fight, not to a points track. The event
      // page would be a detour through the thing nobody came for.
      to={boss ? '/world-boss' : `/events/${event.key}`}
      className={`pxframe event-card event-theme--${event.theme} ${featured ? 'event-card--featured' : ''}`}
    >
      {/* The theme's colour band. This is what makes a Halloween card and a
          Winter card recognisably different without either restyling its own
          text — the accent lives here, and the body stays legible whatever it
          is set to. */}
      <div className="event-card__band" />

      <div className="event-card__body">
        <div className="event-card__head">
          <div className="event-card__name">{event.name}</div>
          {/* PART 35: the label is the point. A World Boss is not another
              festival and the list has to say so before somebody opens it. */}
          {boss && <span className="chip chip--gold">WORLD BOSS</span>}
          {event.status === 'PAUSED' && <span className="chip chip--gold">Paused</span>}
          {event.status === 'ENDED' && <span className="chip">Finished</span>}
        </div>

        <div className="event-card__summary">{event.summary || event.name}</div>

        <div className="event-card__meta">
          {live ? (
            <strong>{countdown(event.secondsRemaining)}</strong>
          ) : event.status === 'SCHEDULED' ? (
            <strong>{untilStart(event.secondsUntilStart)}</strong>
          ) : (
            <span className="muted">Ended {formatDate(event.endsAt)}</span>
          )}
          {/* Only shown once they have some. A zero on an event you have not
              opened yet reads as failure rather than as a starting point. */}
          {boss && (
            <span className="muted">
              {boss.bossName} · {boss.percent.toFixed(0)}% HP
            </span>
          )}
          {!boss && event.myPoints > 0 && (
            <span className="muted">
              {formatNumber(event.myPoints)} {event.pointsName ?? 'pts'}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------------------
   One event
   ------------------------------------------------------------------------- */

export function EventDetailPage() {
  const { key = '' } = useParams();
  const toast = useToast();
  const state = useAsync<EventDetail>(() => api.get(`/events/${key}`), [key]);
  const [busy, setBusy] = useState<string | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (state.loading || !state.data) return <div className="pxwell skeleton" style={{ height: 400 }} />;

  const event = state.data;
  const live = event.status === 'ACTIVE';

  const claim = async (threshold: number) => {
    setBusy(`tier:${threshold}`);
    try {
      const result = await api.post<{ label: string }>(`/events/${key}/claim`, { threshold });
      toast.push('reward', `Collected: ${result.label}`);
      state.reload();
    } catch (error) {
      toast.push('error', error instanceof ApiError ? error.message : 'Could not collect that.');
    } finally {
      setBusy(null);
    }
  };

  const claimQuest = async (questKey: string) => {
    setBusy(`quest:${questKey}`);
    try {
      await api.post(`/events/${key}/quest/${questKey}/claim`, {});
      toast.push('reward', 'Quest reward collected.');
      state.reload();
    } catch (error) {
      toast.push('error', error instanceof ApiError ? error.message : 'Could not collect that.');
    } finally {
      setBusy(null);
    }
  };

  const claimDaily = async (taskKey: string) => {
    setBusy(`daily:${taskKey}`);
    try {
      await api.post(`/events/${key}/daily/${taskKey}/claim`, {});
      toast.push('success', 'Task collected.');
      state.reload();
    } catch (error) {
      toast.push('error', error instanceof ApiError ? error.message : 'Could not collect that.');
    } finally {
      setBusy(null);
    }
  };

  const buy = async (entryKey: string) => {
    setBusy(`shop:${entryKey}`);
    try {
      await api.post(`/events/${key}/shop/${entryKey}`, { quantity: 1 });
      toast.push('success', 'Bought.');
      state.reload();
    } catch (error) {
      toast.push('error', error instanceof ApiError ? error.message : 'Could not buy that.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`stack event-theme--${event.theme}`}>
      <div className="page-head">
        <div>
          <Link to="/events" className="page-head__back">
            ← All events
          </Link>
          <h1 className="page-head__title">{event.name}</h1>
          <div className="page-head__sub">{event.summary}</div>
        </div>
        <div className="event-detail__clock">
          {live ? (
            <>
              <div className="event-detail__countdown">{countdown(event.secondsRemaining)}</div>
              <div className="muted">Ends {formatDate(event.endsAt)}</div>
            </>
          ) : event.status === 'PAUSED' ? (
            <div className="chip chip--gold">Paused by the team</div>
          ) : event.status === 'SCHEDULED' ? (
            <div className="event-detail__countdown">{untilStart(event.secondsUntilStart)}</div>
          ) : (
            <div className="chip">Finished {formatDate(event.endsAt)}</div>
          )}
        </div>
      </div>

      {event.description && (
        <Panel>
          <p className="event-detail__blurb">{event.description}</p>
        </Panel>
      )}

      {/* PART 16's player-facing half. An event that quietly doubles something
          nobody is told about is a balance change, not a celebration. */}
      {event.bonuses.length > 0 && (
        <Panel title="While this event runs">
          <div className="event-bonuses">
            {event.bonuses.map((bonus) => (
              <div key={bonus.key} className="pxwell event-bonus">
                <div className="event-bonus__value">{bonus.multiplier.toFixed(bonus.multiplier % 1 ? 1 : 0)}×</div>
                <div className="event-bonus__label">{BONUS_LABEL[bonus.key] ?? bonus.key}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {(event.pointsName || event.currencyName) && (
        <Panel>
          <div className="event-wallet">
            <div className="stat">
              <div className="stat__value">{formatNumber(event.wallet.points)}</div>
              <div className="stat__label">{event.pointsName ?? 'Points'}</div>
            </div>
            {event.currencyName && (
              <div className="stat">
                <div className="stat__value">{formatNumber(event.wallet.currency)}</div>
                <div className="stat__label">{event.currencyName}</div>
              </div>
            )}
          </div>
        </Panel>
      )}

      {event.objectives.length > 0 && (
        <Panel title="How to earn" hint={`${event.objectives.filter((o) => o.complete).length} / ${event.objectives.length}`}>
          <div className="stack-sm">
            {event.objectives.map((objective) => (
              <ObjectiveRow key={objective.key} objective={objective} pointsName={event.pointsName} />
            ))}
          </div>
        </Panel>
      )}

      {event.dailies.length > 0 && (
        <Panel title="Today's tasks" collapsibleKey={`event-${key}-dailies`}>
          <div className="stack-sm">
            {event.dailies.map((task) => (
              <div key={task.key} className="pxwell event-task">
                <div className="event-task__main">
                  <div>{task.name}</div>
                  <Bar ratio={task.target > 0 ? Math.min(1, task.count / task.target) : 0} />
                </div>
                {task.claimed ? (
                  <span className="chip chip--leaf">Collected</span>
                ) : task.complete ? (
                  <Button onClick={() => claimDaily(task.key)} disabled={busy === `daily:${task.key}`}>
                    Collect
                  </Button>
                ) : (
                  <span className="muted">
                    {task.count} / {task.target}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {event.quests.length > 0 && (
        <Panel title="Quests">
          <div className="stack-sm">
            {event.quests.map((quest) => (
              <div key={quest.key} className="pxwell stack-sm">
                <div className="event-quest__head">
                  <strong>{quest.name}</strong>
                  {quest.claimed ? (
                    <span className="chip chip--leaf">Collected</span>
                  ) : quest.complete ? (
                    <Button onClick={() => claimQuest(quest.key)} disabled={busy === `quest:${quest.key}`}>
                      Collect
                    </Button>
                  ) : (
                    <span className="muted">
                      Step {quest.stepsDone + 1} of {quest.steps.length}
                    </span>
                  )}
                </div>
                {quest.description && <div className="muted">{quest.description}</div>}
                <ol className="event-quest__steps">
                  {quest.steps.map((step) => (
                    <li
                      key={step.step}
                      className={`event-quest__step ${step.done ? 'is-done' : ''} ${
                        step.current ? 'is-current' : ''
                      }`}
                    >
                      {step.name}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {event.communityGoals.map((goal) => (
        <CommunityGoal key={goal.key} goal={goal} />
      ))}

      {event.tiers.length > 0 && (
        <Panel title="Rewards">
          <div className="stack-sm">
            {event.tiers.map((tier) => (
              <TierRow
                key={tier.threshold}
                tier={tier}
                pointsName={event.pointsName}
                points={event.wallet.points}
                live={live}
                busy={busy === `tier:${tier.threshold}`}
                onClaim={() => claim(tier.threshold)}
              />
            ))}
          </div>
        </Panel>
      )}

      {event.shop.length > 0 && (
        <Panel title={`${event.currencyName ?? 'Event'} shop`}>
          <div className="event-grid">
            {event.shop.map((entry) => (
              <div key={entry.key} className="pxwell event-shop-item">
                <div className="event-shop-item__name">{entry.reward?.label ?? entry.key}</div>
                <div className="event-shop-item__cost">
                  {formatNumber(entry.cost)} {event.currencyName ?? 'points'}
                </div>
                {entry.stockRemaining != null && (
                  <div className="muted">{entry.stockRemaining} left</div>
                )}
                <Button
                  onClick={() => buy(entry.key)}
                  disabled={!live || entry.soldOut || busy === `shop:${entry.key}`}
                >
                  {entry.soldOut ? 'Sold out' : 'Buy'}
                </Button>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {event.eggFamilies.length > 0 && (
        <Panel title="Event eggs">
          <div className="event-grid">
            {event.eggFamilies.map((family) => (
              <div key={family.key} className="pxwell event-egg">
                {/* The server sends the shell colours the same way the Egg
                    Market does, so the egg an event advertises is drawn by the
                    same component that draws it on the shelf. */}
                <EggArt appearance={family.appearance ?? null} size="md" />
                <div>{family.name}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {event.leaderboard.length > 0 && (
        <Panel title="Leaderboard" collapsibleKey={`event-${key}-board`}>
          <ol className="event-board">
            {event.leaderboard.map((row) => (
              <li key={row.rank} className={`event-board__row ${row.isMe ? 'is-me' : ''}`}>
                <span className="event-board__rank">{row.rank}</span>
                <Link to={`/trainer/${row.username}`}>{row.username}</Link>
                <span className="event-board__points">{formatNumber(row.points)}</span>
              </li>
            ))}
          </ol>
        </Panel>
      )}
    </div>
  );
}

function ObjectiveRow({
  objective,
  pointsName,
}: {
  objective: EventObjectiveDto;
  pointsName: string | null;
}) {
  // A repeatable objective with a target of one has no meaningful bar — it is
  // "every hatch earns 10 points", not a goal of one hatch. Drawing 0 / 1 for
  // it reads as a task nobody has started rather than as a standing rate, which
  // is exactly what the migrated pre-framework objectives all look like.
  const isRate = objective.repeatable && objective.target <= 1;

  return (
    <div className={`pxwell event-objective ${objective.complete && !isRate ? 'is-complete' : ''}`}>
      <div className="event-objective__main">
        <div className="event-objective__name">
          {objective.name}
          {objective.complete && !isRate && <span className="chip chip--leaf">Done</span>}
        </div>
        {!isRate && (
          <Bar ratio={objective.target > 0 ? Math.min(1, objective.count / objective.target) : 0} />
        )}
      </div>
      <div className="event-objective__meta">
        <span className="event-objective__count">
          {isRate
            ? `${formatNumber(objective.count)} so far`
            : `${formatNumber(objective.count)} / ${formatNumber(objective.target)}`}
        </span>
        {objective.pointsEach > 0 && (
          <span className="muted">
            +{objective.pointsEach} {pointsName ?? 'pts'} each
          </span>
        )}
        {/* Stated rather than discovered. A player who has hit a daily cap and
            is not told simply thinks the event is broken. */}
        {objective.dailyLimit != null && (
          <span className="muted">
            {objective.countToday} / {objective.dailyLimit} today
          </span>
        )}
      </div>
    </div>
  );
}

function TierRow({
  tier,
  pointsName,
  points,
  live,
  busy,
  onClaim,
}: {
  tier: EventTierDto;
  pointsName: string | null;
  points: number;
  live: boolean;
  busy: boolean;
  onClaim: () => void;
}) {
  return (
    <div className={`pxwell event-tier ${tier.claimed ? 'is-claimed' : ''} ${tier.earned ? 'is-earned' : ''}`}>
      <div className="event-tier__threshold">
        {formatNumber(tier.threshold)}
        <span>{pointsName ?? 'points'}</span>
      </div>

      <div className="event-tier__main">
        <div className="event-tier__label">{tier.label}</div>
        {/* A reward whose label is already the tier's label says it twice.
            That is the common case for anything converted from the old reward
            columns, where the tier label *was* the description of the reward. */}
        <div className="event-tier__rewards">
          {tier.rewards
            .filter((reward) => reward.label.trim() !== tier.label.trim())
            .map((reward) => (
              <RewardChip key={reward.key} reward={reward} />
            ))}
        </div>
        {!tier.earned && <Bar ratio={tier.threshold > 0 ? Math.min(1, points / tier.threshold) : 0} />}
      </div>

      <div className="event-tier__action">
        {tier.claimed ? (
          <span className="chip chip--leaf">Collected</span>
        ) : tier.earned ? (
          <Button onClick={onClaim} disabled={!live || busy}>
            {busy ? '…' : 'Collect'}
          </Button>
        ) : (
          <span className="muted">{formatNumber(tier.threshold - points)} to go</span>
        )}
      </div>
    </div>
  );
}

function RewardChip({ reward }: { reward: EventRewardDefDto }) {
  return (
    <span className={`chip event-reward event-reward--${reward.kind.toLowerCase()}`}>
      {reward.label}
      {reward.quantity > 1 && ` ×${reward.quantity}`}
    </span>
  );
}

function CommunityGoal({ goal }: { goal: EventCommunityGoalDto }) {
  return (
    <Panel title={goal.name} hint={goal.complete ? 'Complete' : undefined}>
      <div className="stack-sm">
        <Bar ratio={goal.target > 0 ? Math.min(1, goal.progress / goal.target) : 0} />
        <div className="event-goal__meta">
          <strong>
            {formatNumber(goal.progress)} / {formatNumber(goal.target)}
          </strong>
          {goal.reward && <RewardChip reward={goal.reward} />}
        </div>
        {/* PART 12: the qualifying rule is stated, because "everyone helped and
            I got nothing" is the failure mode of a silent minimum. */}
        <div className="muted">
          {goal.qualified
            ? `You have helped ${formatNumber(goal.myContribution)} — you qualify for the reward.`
            : `Help at least ${formatNumber(goal.minContribution)} to qualify. You have helped ${formatNumber(
                goal.myContribution,
              )}.`}
        </div>
        {goal.complete && goal.claimed && <span className="chip chip--leaf">Reward collected</span>}
      </div>
    </Panel>
  );
}
